/* ==========================================================================
   PDP animations — scroll reveal, staggered entrances and stat count-ups.

   Progressive enhancement & accessibility:
   - The CSS only hides reveal targets under `html.pdp-js`, and this script is
     the only thing that adds that class. So if the script never loads/runs,
     nothing is ever left hidden.
   - Bails out entirely when the visitor prefers reduced motion.
   - Re-scans sections re-rendered by the Shopify theme editor.
   ========================================================================== */
(() => {
  if (window.__pdpAnimInit) return;
  window.__pdpAnimInit = true;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  document.documentElement.classList.add('pdp-js');

  const SINGLES = '.pdp-head, .pdp-split__media, .pdp-story__media';
  const GROUPS =
    '.pdp-grid, .pdp-steps, .pdp-testi__grid, .pdp-featurelist, .pdp-logos, .pdp-story__stats, .pdp-faqs';
  const COUNTERS = '.pdp-fcard__stat, .pdp-story__stat-num';

  const supported = 'IntersectionObserver' in window;

  const io = supported
    ? new IntersectionObserver(
        (entries, obs) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const el = entry.target;
            obs.unobserve(el);
            if (el.dataset.pdpCount !== undefined) countUp(el);
            else el.classList.add('pdp-in');
          }
        },
        { threshold: 0.16, rootMargin: '0px 0px -8% 0px' }
      )
    : null;

  function reveal(el, cls) {
    if (el.classList.contains(cls)) return;
    el.classList.add(cls);
    if (io) io.observe(el);
    else el.classList.add('pdp-in');
  }

  function markCounter(el) {
    if (el.dataset.pdpInit) return;
    el.dataset.pdpInit = '1';
    const match = /^(\D*?)(\d[\d,]*)(.*)$/s.exec(el.textContent.trim());
    if (!match) return;

    const grouped = /,/.test(match[2]);
    el.dataset.pdpCount = '';
    el.dataset.pdpPrefix = match[1];
    el.dataset.pdpTarget = match[2].replace(/,/g, '');
    el.dataset.pdpSuffix = match[3];
    el.dataset.pdpGroup = grouped ? '1' : '0';

    if (io) {
      el.textContent = match[1] + (grouped ? (0).toLocaleString() : '0') + match[3];
      io.observe(el);
    }
  }

  function countUp(el) {
    const target = parseInt(el.dataset.pdpTarget, 10);
    const prefix = el.dataset.pdpPrefix || '';
    const suffix = el.dataset.pdpSuffix || '';
    const grouped = el.dataset.pdpGroup === '1';
    const fmt = (n) => (grouped ? n.toLocaleString() : String(n));
    const duration = 1300;
    let started;

    const step = (now) => {
      if (started === undefined) started = now;
      const t = Math.min(1, (now - started) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = prefix + fmt(Math.round(eased * target)) + suffix;
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  /* ---- FAQ accordion: smooth open AND close (height + fade) -------------- */
  const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

  // fill:'forwards' holds the end frame until we commit the resting inline style
  // and cancel(), so the panel never reverts to the pre-finish inline height for
  // a frame (the classic WAAPI end-of-animation flash). overflow is toggled only
  // during the animation so a focused link in an open answer isn't clipped.
  function openFaq(faq, panel) {
    faq.classList.add('pdp-faq--open');
    faq.open = true;
    panel.style.overflow = 'hidden';
    panel.style.height = '0px';
    const full = panel.scrollHeight;
    const anim = panel.animate(
      [
        { height: '0px', opacity: 0 },
        { height: full + 'px', opacity: 1 },
      ],
      { duration: 320, easing: EASE, fill: 'forwards' }
    );
    return anim.finished
      .then(() => {
        panel.style.height = '';
        panel.style.overflow = '';
        anim.cancel();
      })
      .catch(() => {});
  }

  function closeFaq(faq, panel) {
    faq.classList.remove('pdp-faq--open');
    panel.style.overflow = 'hidden';
    const full = panel.scrollHeight;
    const anim = panel.animate(
      [
        { height: full + 'px', opacity: 1 },
        { height: '0px', opacity: 0 },
      ],
      { duration: 280, easing: EASE, fill: 'forwards' }
    );
    return anim.finished
      .then(() => {
        faq.open = false;
        panel.style.height = '';
        panel.style.overflow = '';
        anim.cancel();
      })
      .catch(() => {});
  }

  function setupFaq(faq) {
    if (faq.dataset.pdpFaq) return;
    faq.dataset.pdpFaq = '1';
    const summary = faq.querySelector('.pdp-faq__summary');
    const panel = faq.querySelector('.pdp-faq__reveal');
    if (!summary || !panel || typeof faq.animate !== 'function') return;

    // Marks this FAQ as JS-controlled so the icon CSS keys off .pdp-faq--open;
    // FAQs where we bail (no WAAPI) keep the native [open] icon behaviour.
    faq.classList.add('pdp-faq--js');
    if (faq.open) faq.classList.add('pdp-faq--open');

    summary.addEventListener('click', (event) => {
      event.preventDefault();
      if (faq.dataset.pdpBusy) return;
      faq.dataset.pdpBusy = '1';
      const done = faq.open ? closeFaq(faq, panel) : openFaq(faq, panel);
      done.then(() => {
        delete faq.dataset.pdpBusy;
      });
    });

    // Keep the icon in sync if the panel is opened/closed without a summary
    // click (e.g. Chrome find-in-page expands a <details> by setting the open
    // attribute, which fires 'toggle', not 'click').
    faq.addEventListener('toggle', () => {
      if (faq.dataset.pdpBusy) return;
      faq.classList.toggle('pdp-faq--open', faq.open);
    });
  }

  function scan(scope) {
    scope.querySelectorAll(SINGLES).forEach((el) => reveal(el, 'pdp-reveal'));
    scope.querySelectorAll(GROUPS).forEach((el) => reveal(el, 'pdp-stagger'));
    scope.querySelectorAll(COUNTERS).forEach((el) => markCounter(el));
    scope.querySelectorAll('.pdp-faq').forEach(setupFaq);
  }

  const run = () => scan(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }

  // Theme editor: re-scan a section after it is re-rendered.
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
